import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { AppState, Route, UserPreferences } from '../types';
import { defaultUserPreferences } from '../data/defaultUserPreferences';

// Action types
type AppAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | undefined }
  | { type: 'SET_CURRENT_ROUTE'; payload: Route | undefined }
  | { type: 'UPDATE_PREFERENCES'; payload: Partial<UserPreferences> }
  | { type: 'CLEAR_ROUTE' }
  | { type: 'RESET_ONBOARDING' };

// Initial state
const initialState: AppState = {
  currentRoute: undefined,
  userPreferences: defaultUserPreferences,
  isLoading: false,
  error: undefined,
};

// Reducer
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_CURRENT_ROUTE':
      return { ...state, currentRoute: action.payload };
    case 'UPDATE_PREFERENCES':
      return {
        ...state,
        userPreferences: { ...state.userPreferences, ...action.payload },
      };
    case 'CLEAR_ROUTE':
      return { ...state, currentRoute: undefined };
    case 'RESET_ONBOARDING':
      return {
        ...state,
        userPreferences: {
          ...state.userPreferences,
          hasCompletedOnboarding: false,
          hasCompletedPreferenceOnboarding: false
        }
      };
    default:
      return state;
  }
}

// Context
const AppStateContext = createContext<{
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
} | null>(null);

// Provider component
export function AppStateProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return (
    <AppStateContext.Provider value={{ state, dispatch }}>
      {children}
    </AppStateContext.Provider>
  );
}

// Custom hook to use the app state
export function useAppState() {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error('useAppState must be used within an AppStateProvider');
  }
  return context;
}

// Action creators
export const appActions = {
  setLoading: (loading: boolean) => ({ type: 'SET_LOADING' as const, payload: loading }),
  setError: (error: string | undefined) => ({ type: 'SET_ERROR' as const, payload: error }),
  setCurrentRoute: (route: Route | undefined) => ({ type: 'SET_CURRENT_ROUTE' as const, payload: route }),
  updatePreferences: (preferences: Partial<UserPreferences>) => ({ type: 'UPDATE_PREFERENCES' as const, payload: preferences }),
  clearRoute: () => ({ type: 'CLEAR_ROUTE' as const }),
};
